import { ethers } from "ethers";
import { useEffect, useState } from "react";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import VestingABI from "src/assets/abis/vault/VestingABI.json";
import StakingABI from "src/assets/abis/vault/StakingABI.json";
import { BscVaultConfig, EthVaultConfig, FtmVaultConfig } from "src/config/balance/vaultBalanceConfig";
import { convertBigNumber } from "src/ether-utils/utils";

export default function useVaultRewardBalance(accountAddress) {
  const [vestingBalance, setVestingBalance] = useState({});
  const [stakedBalance, setStakedBalance] = useState({});

  const { bscProvider, ftmProvider, ethProvider } = useProviderContext();

  const _fetchSingleVestingBalance = async (_contract, accountAddress, reserve) => {
    const rewardRaw = await _contract.getClaimableReward(accountAddress, reserve);
    return convertBigNumber(rewardRaw, 18, 2);
  };

  const _fetchVestingVaultBalance = async (accountAddress, vestingConfig, _provider) => {
    let promiseList = [];
    for (var contractConfig of vestingConfig) {
      const { contractAddress, reserves } = contractConfig;
      const vestingContract = new ethers.Contract(contractAddress, VestingABI, _provider);
      for (var reserve of reserves)
        promiseList.push(_fetchSingleVestingBalance(vestingContract, accountAddress, reserve));
    }
    const promiseResult = await Promise.all(promiseList);
    if (promiseResult.length === 0) return 0;
    return promiseResult.reduce((x, y) => x + y);
  };

  const _fetchSingleStakedVaultBalance = async (_contract, accountAddress) => {
    const rewardRaw = await _contract.getTotalRewardsBalance(accountAddress);
    return convertBigNumber(rewardRaw, 18, 2);
  };

  const _fetchStakedVaultBalance = async (accountAddress, stakedConfig, _provider) => {
    let promiseList = [];
    for (var stakeAddress of stakedConfig) {
      const stakedContract = new ethers.Contract(stakeAddress, StakingABI, _provider);
      promiseList.push(_fetchSingleStakedVaultBalance(stakedContract, accountAddress));
    }
    const promiseResult = await Promise.all(promiseList);
    if (promiseResult.length === 0) return 0;
    return promiseResult.reduce((x, y) => x + y);
  };

  const _fetchAllVaultBalance = async (accountAddress) => {
    const { vesting: bscVesting, staked: bscStaked } = BscVaultConfig;
    const { vesting: ftmVesting, staked: ftmStaked } = FtmVaultConfig;
    const { vesting: ethVesting, staked: ethStaked } = EthVaultConfig;

    let [bscVestingTrava, ftmVestingTrava, ethVestingTrava, bscStakedTrava, ftmStakedTrava, ethStakedTrava] =
      await Promise.all([
        _fetchVestingVaultBalance(accountAddress, bscVesting, bscProvider),
        _fetchVestingVaultBalance(accountAddress, ftmVesting, ftmProvider),
        _fetchVestingVaultBalance(accountAddress, ethVesting, ethProvider),
        _fetchStakedVaultBalance(accountAddress, bscStaked, bscProvider),
        _fetchStakedVaultBalance(accountAddress, ftmStaked, ftmProvider),
        _fetchStakedVaultBalance(accountAddress, ethStaked, ethProvider),
      ]);
    return { bscVestingTrava, ftmVestingTrava, ethVestingTrava, bscStakedTrava, ftmStakedTrava, ethStakedTrava };
  };

  useEffect(() => {
    if (bscProvider && ftmProvider && ethProvider) {
      _fetchAllVaultBalance(accountAddress)
        .then((data) => {
          const { bscVestingTrava, ftmVestingTrava, ethVestingTrava, bscStakedTrava, ftmStakedTrava, ethStakedTrava } =
            data;
          setVestingBalance({ bscVestingTrava, ftmVestingTrava, ethVestingTrava });
          setStakedBalance({ bscStakedTrava, ftmStakedTrava, ethStakedTrava });
        })
        .catch((error) => {
          console.error("valut balance error: ", error);
        });
    }
  }, [bscProvider, ftmProvider, ethProvider, accountAddress]);

  return { vestingBalance, stakedBalance };
}
